/**
 * LocalStorage adapter for event queue fallback
 * Provides persistent storage when IndexedDB is unavailable
 * 
 * Optimized for performance:
 * - Lazy initialization (reads from storage only when needed)
 * - Batched writes to minimize localStorage access
 * - Minimized synchronous operations
 * 
 * Requirements: 1.7 - LocalStorage fallback implementation
 */

import { EventQueue, StoredEvent } from '../core/EventQueue';
import { TrackingEvent } from '../types/events';

/**
 * LocalStorage-based implementation of EventQueue
 * Falls back from IndexedDB when unavailable
 */
export class LocalStorageAdapter implements EventQueue {
  private readonly storageKey = 'tracking-events';
  private readonly maxSize = 1000;
  
  // Lazy initialization support
  private cache: StoredEvent[] | null = null;
  private dirty = false;
  
  // Batching support
  private writeTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly writeBatchDelayMs = 10; // Batch writes within 10ms window

  /**
   * Generate a unique ID for an event
   * Uses high-resolution timestamp for uniqueness without crypto overhead
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Get cached events, loading from storage lazily on first access
   * Returns empty array if storage is unavailable or corrupted
   */
  private getCache(): StoredEvent[] {
    if (this.cache === null) {
      this.cache = this.readStorage();
    }
    return this.cache;
  }

  /**
   * Read all stored events from localStorage
   * Returns empty array if storage is unavailable or corrupted
   */
  private readStorage(): StoredEvent[] {
    try {
      const data = localStorage.getItem(this.storageKey);
      if (!data) {
        return [];
      }
      return JSON.parse(data) as StoredEvent[];
    } catch (error) {
      console.warn('Failed to read from localStorage:', error);
      return [];
    }
  }

  /**
   * Schedule a batched write to localStorage
   * Debounces multiple operations to reduce I/O overhead
   */
  private scheduleWrite(): void {
    if (this.writeTimeout) {
      return; // Already scheduled
    }

    this.writeTimeout = setTimeout(() => {
      this.writeTimeout = null;
      if (this.dirty && this.cache !== null) {
        this.writeStorage(this.cache);
        this.dirty = false;
      }
    }, this.writeBatchDelayMs);
  }

  /**
   * Write events to localStorage immediately
   * Handles quota exceeded errors gracefully
   */
  private writeStorage(events: StoredEvent[]): void {
    try {
      const serialized = JSON.stringify(events);
      localStorage.setItem(this.storageKey, serialized);
    } catch (error) {
      // Handle quota exceeded errors
      if (error instanceof DOMException && 
          (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED')) {
        console.warn('localStorage quota exceeded, attempting to free space');
        
        // Try removing oldest 100 events and retry
        if (events.length > 100) {
          const trimmedEvents = events.slice(100);
          try {
            const serialized = JSON.stringify(trimmedEvents);
            localStorage.setItem(this.storageKey, serialized);
            console.warn(`Removed ${100} oldest events due to quota limits`);
            // Update cache to match what was written
            this.cache = trimmedEvents;
            return;
          } catch (retryError) {
            console.error('Failed to write to localStorage even after trimming:', retryError);
          }
        } else {
          console.error('localStorage quota exceeded and cannot trim further');
        }
      } else {
        console.error('Failed to write to localStorage:', error);
      }
    }
  }

  /**
   * Enqueue an event for later transmission
   * Optimized for minimal latency - uses batched writes
   * Enforces max size limit by evicting oldest events
   */
  async enqueue(event: TrackingEvent): Promise<void> {
    const events = this.getCache();
    
    const storedEvent: StoredEvent = {
      id: this.generateId(),
      event,
      timestamp: Date.now(),
      retryCount: 0,
      lastAttemptTimestamp: null,
    };

    events.push(storedEvent);

    // Enforce max size by removing oldest events
    if (events.length > this.maxSize) {
      const evictCount = 100; // Remove oldest 100 events
      events.splice(0, evictCount);
      console.warn(`Event queue exceeded max size (${this.maxSize}), evicted ${evictCount} oldest events`);
    }

    // Mark cache as dirty and schedule batched write
    this.dirty = true;
    this.scheduleWrite();
  }

  /**
   * Dequeue events (retrieve and remove)
   * Returns up to `count` events from the front of the queue
   */
  async dequeue(count: number): Promise<TrackingEvent[]> {
    const events = this.getCache();
    const toDequeue = events.splice(0, count);
    this.dirty = true;
    this.scheduleWrite();
    return toDequeue.map(stored => stored.event);
  }

  /**
   * Peek at events without removing them
   * Returns up to `count` events from the front of the queue
   */
  async peek(count: number): Promise<TrackingEvent[]> {
    const events = this.getCache();
    const toPeek = events.slice(0, count);
    return toPeek.map(stored => stored.event);
  }

  /**
   * Remove specific events by ID
   * Used after successful transmission
   */
  async remove(eventIds: string[]): Promise<void> {
    const events = this.getCache();
    const idSet = new Set(eventIds);
    const initialLength = events.length;
    
    // Filter out removed events
    const filtered = events.filter(stored => !idSet.has(stored.id));
    
    // Only write if something changed
    if (filtered.length !== initialLength) {
      this.cache = filtered;
      this.dirty = true;
      this.scheduleWrite();
    }
  }

  /**
   * Get current queue size
   */
  async size(): Promise<number> {
    const events = this.getCache();
    return events.length;
  }

  /**
   * Flush any pending writes to localStorage immediately
   * Useful for testing or before critical operations
   */
  async flush(): Promise<void> {
    if (this.writeTimeout) {
      clearTimeout(this.writeTimeout);
      this.writeTimeout = null;
    }
    
    if (this.dirty && this.cache !== null) {
      this.writeStorage(this.cache);
      this.dirty = false;
    }
  }

  /**
   * Clear all events from the queue
   */
  async clear(): Promise<void> {
    try {
      localStorage.removeItem(this.storageKey);
      this.cache = [];
      this.dirty = false;
      if (this.writeTimeout) {
        clearTimeout(this.writeTimeout);
        this.writeTimeout = null;
      }
    } catch (error) {
      console.error('Failed to clear localStorage:', error);
    }
  }
}
